import { createContext, useContext, useEffect, useLayoutEffect, useMemo, useRef, useState, type FormEvent, type ReactNode } from "react";
import { Link, Navigate, useLocation, useNavigate } from "react-router-dom";
import { Icon } from "../../app/Icon";
import { BrandLogo } from "../../app/BrandLogo";
import { GoogleSignIn } from "./GoogleSignIn";
import { useStudioTheme } from "../ai/useStudioTheme";
import type { StudioTheme } from "../ai/useStudioTheme";
import { AccountMenu } from "./AccountMenu";
import "./auth.css";
import { authDestination } from "./pricing";
import { useAnalytics } from "../analytics/analytics";

export type Account = Readonly<{ id?: string; email: string; name: string; paid: boolean; credits?: number; plan?: string | null; billingInterval?: string | null; artists?: number | null; currentPeriodEnd?: string | null; cancelAtPeriodEnd?: boolean; manageUrl?: string | null; upgradePending?: boolean; trialUsed: number; trialRemaining: number }>;
export type GoogleSignInResult = Readonly<{ code: string; message: string }> | null;
type AuthContextValue = Readonly<{
  account: Account | null;
  loading: boolean;
  authAvailable: boolean;
  signIn: (email: string, password: string) => Promise<string | null>;
  signUp: (name: string, email: string, password: string) => Promise<string | null>;
  signInGoogle: (credential: string, password?: string) => Promise<GoogleSignInResult>;
  signOut: () => Promise<void>;
  clearAccount: () => void;
  refreshAccount: () => Promise<Account | null>;
}>;

const AuthContext = createContext<AuthContextValue | null>(null);

async function errorMessage(response: Response, fallback: string): Promise<string> {
  if (response.status === 429) return "Too many attempts. Please wait 15 minutes before trying again.";
  if (response.status >= 500) return "The account service is temporarily unavailable. Please try again shortly.";
  const body = await response.json().catch(() => null) as { error?: { code?: string } } | null;
  const messages: Record<string, string> = {
    invalid_credentials: "Email or password is incorrect. Check your details and try again.",
    email_in_use: "We couldn’t create an account with those details. Try signing in or resetting your password.",
    weak_password: "Use a password between 12 and 128 characters.",
    invalid_reset_token: "This reset link has expired or has already been used. Request a new link.",
    invalid_email: "Enter a valid email address.",
    origin_denied: "This request could not be verified. Reload the page and try again.",
  };
  return messages[body?.error?.code ?? ""] ?? fallback;
}

export function AuthProvider({ children }: { children: ReactNode }) {
  const analytics = useAnalytics();
  const [account, setAccount] = useState<Account | null>(null);
  const [loading, setLoading] = useState(true);
  const [authAvailable, setAuthAvailable] = useState(true);
  const sessionVersion = useRef(0);

  useEffect(() => {
    let active = true;
    const version = sessionVersion.current;
    void fetch("/api/v1/auth/me", { credentials: "same-origin", signal: AbortSignal.timeout(15_000) })
      .then(async (response) => {
        const available = response.headers.get("content-type")?.includes("application/json") === true;
        if (active) setAuthAvailable(available);
        if (response.ok && available) {
          const body = await response.json() as { user: Account };
          if (active && version === sessionVersion.current) setAccount(body.user);
        }
      })
      // A transient account-service failure must keep the creator locked. Only
      // an HTML response identifies a static preview with no auth API at all.
      .catch(() => undefined)
      .finally(() => { if (active) setLoading(false); });
    return () => { active = false; };
  }, []);

  const value = useMemo<AuthContextValue>(() => ({
    account,
    loading,
    authAvailable,
    clearAccount() { sessionVersion.current++; setAccount(null); },
    async refreshAccount() {
      const version = sessionVersion.current;
      const response = await fetch("/api/v1/auth/me", { credentials: "same-origin", signal: AbortSignal.timeout(15_000) });
      if (version !== sessionVersion.current) return null;
      if (!response.ok) { if (response.status === 401) setAccount(null); return null; }
      const user = (await response.json() as { user: Account }).user;
      if (version !== sessionVersion.current) return null;
      sessionVersion.current++;
      setAccount(user);
      return user;
    },
    async signIn(email, password) {
      const response = await fetch("/api/v1/auth/signin", { method: "POST", credentials: "same-origin", signal: AbortSignal.timeout(15_000), headers: { "Content-Type": "application/json" }, body: JSON.stringify({ email, password }) });
      if (!response.ok) return errorMessage(response, "Sign in could not be completed.");
      const user = (await response.json() as { user: Account }).user;
      sessionVersion.current++;
      if (user.id) analytics.identify(user.id);
      analytics.track("signed_in", { auth_method: "email" });
      setAccount(user);
      return null;
    },
    async signUp(name, email, password) {
      const response = await fetch("/api/v1/auth/signup", { method: "POST", credentials: "same-origin", signal: AbortSignal.timeout(15_000), headers: { "Content-Type": "application/json" }, body: JSON.stringify({ name, email, password }) });
      if (!response.ok) return errorMessage(response, "Sign up could not be completed.");
      const user = (await response.json() as { user: Account }).user;
      sessionVersion.current++;
      if (user.id) analytics.identify(user.id);
      analytics.track("account_created", { auth_method: "email" });
      setAccount(user);
      return null;
    },
    async signOut() {
      const response = await fetch("/api/v1/auth/signout", { method: "POST", credentials: "same-origin", signal: AbortSignal.timeout(15_000), headers: { "Content-Type": "application/json" }, body: "{}" });
      if (!response.ok) throw new Error("Sign out failed");
      analytics.track("signed_out");
      analytics.reset();
      sessionVersion.current++;
      setAccount(null);
    },
    async signInGoogle(credential, password) {
      const response = await fetch("/api/v1/auth/google", { method: "POST", credentials: "same-origin", signal: AbortSignal.timeout(15_000), headers: { "Content-Type": "application/json" }, body: JSON.stringify({ credential, ...(password === undefined ? {} : { password }) }) });
      if (!response.ok) {
        const body = await response.json().catch(() => null) as { error?: { code?: string } } | null;
        const code = body?.error?.code ?? "service_unavailable";
        const messages: Record<string, string> = {
          invalid_credentials: "That password is incorrect. Try again or reset your password.",
          invalid_google_credential: "Google sign-in expired or could not be verified. Try Google again.",
          google_account_mismatch: "This email is connected to another Google account. Continue with email.",
          google_link_required: "Confirm your existing account password to connect Google.",
        };
        return { code, message: response.status === 429 ? "Too many attempts. Please wait 15 minutes before trying again." : messages[code] ?? "Google sign-in is currently unavailable. Please continue with email." };
      }
      const user = (await response.json() as { user: Account }).user;
      sessionVersion.current++;
      if (user.id) analytics.identify(user.id);
      setAccount(user);
      return null;
    },
  }), [account, analytics, authAvailable, loading]);

  useEffect(() => {
    if (!account) return;
    const refresh = () => { if (document.visibilityState === "visible") void value.refreshAccount().catch(() => undefined); };
    window.addEventListener("focus", refresh);
    document.addEventListener("visibilitychange", refresh);
    return () => { window.removeEventListener("focus", refresh); document.removeEventListener("visibilitychange", refresh); };
  }, [account, value]);

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

// The provider and its hook intentionally live together so every auth mutation
// shares the same session-version guard.
// eslint-disable-next-line react-refresh/only-export-components
export function useAuth() {
  const value = useContext(AuthContext);
  if (!value) throw new Error("AuthProvider is missing");
  return value;
}

export function AuthNav({ theme, onToggleTheme }: { theme: StudioTheme; onToggleTheme: () => void }) {
  const { account, loading, signOut } = useAuth();
  const location = useLocation();
  const from = location.pathname === "/billing" ? `${location.pathname}${location.search}` : (location.state as { from?: string } | null)?.from;
  if (loading) return <span className="auth-nav-user" role="status">Checking session…</span>;
  return account
    ? <AccountMenu account={account} theme={theme} onToggleTheme={onToggleTheme} onSignOut={signOut} />
    : <Link className="auth-nav-link auth-nav-link--accent" to="/sign-in" state={{ from: authDestination(from) }}>Sign in</Link>;
}

function AuthShell({ children }: { children: ReactNode }) {
  const { theme, toggleTheme } = useStudioTheme();
  return <div className="auth-studio studio-theme" data-theme={theme}>
    <main className="auth-page">
      <aside className="auth-banner" aria-label="Made for tattoo artists">
        <div className="auth-banner__content">
          <span className="auth-banner__badge"><Icon name="spark" /> Made for tattoo artists</span>
          <h2>Less tracing.<br /><em className="studio-heading-accent">More tattooing.</em></h2>
          <p>From your first reference to your next great piece. Create clean stencils and make them yours.</p>
        </div>
      </aside>
      <section className="auth-panel" aria-label="Your Stencil Studio account">
        <div className="auth-panel__controls">
          <button className="auth-theme-toggle" type="button" aria-label={`Switch to ${theme === "dark" ? "light" : "dark"} theme`} onClick={toggleTheme}><Icon name={theme === "dark" ? "sun" : "moon"} /></button>
          <Link className="auth-close" to="/" aria-label="Close and return home"><Icon name="close" /></Link>
        </div>
        <div className="auth-card">
          <header className="auth-brand-header"><Link className="auth-logo" to="/ai" aria-label="Stencil Studio creator"><BrandLogo tone={theme === "dark" ? "light" : "dark"} /></Link></header>
          {children}
        </div>
      </section>
    </main>
  </div>;
}

type AuthMode = "sign-in" | "sign-up" | "forgot" | "reset";
type Field = "name" | "email" | "password";
type FieldErrors = Partial<Record<Field, string>>;

function AuthFormPage({ mode }: { mode: AuthMode }) {
  const { account, loading, signIn, signUp, signInGoogle, clearAccount } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const state = location.state as { from?: string; message?: string; email?: string } | null;
  const [values, setValues] = useState({ name: "", email: typeof state?.email === "string" ? state.email : "", password: "" });
  const [fields, setFields] = useState<FieldErrors>({});
  const [error, setError] = useState<string | null>(null);
  const [message, setMessage] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const [googleBusy, setGoogleBusy] = useState(false);
  const [emailOpen, setEmailOpen] = useState(mode === "forgot" || mode === "reset" || Boolean(state?.email) || Boolean(state?.message));
  const emailRef = useRef<HTMLInputElement>(null);
  const [visible, setVisible] = useState(false);
  const pending = useRef(false);
  const alertRef = useRef<HTMLDivElement>(null);
  const token = new URLSearchParams(location.search).get("token") ?? "";
  const signup = mode === "sign-up";
  const recovery = mode === "forgot" || mode === "reset";
  const newPassword = signup || mode === "reset";
  const invalidLink = mode === "reset" && !token;
  const destination = authDestination(state?.from ?? new URLSearchParams(location.search).get("from"));
  const analytics = useAnalytics();
  useLayoutEffect(() => { if (emailOpen) emailRef.current?.focus(); }, [emailOpen]);
  useEffect(() => { if (error) alertRef.current?.focus(); }, [error]);
  useEffect(() => {
    document.title = `${{ "sign-in": "Sign in", "sign-up": "Create account", forgot: "Reset password", reset: "Choose a new password" }[mode]} · Stencil Studio`;
  }, [mode]);
  useEffect(() => {
    if (mode === "sign-in" || mode === "sign-up") analytics.track("authentication_started", { auth_mode: mode });
  }, [analytics, mode]);

  const update = (field: Field, value: string) => {
    setValues((previous) => ({ ...previous, [field]: value }));
    setFields((previous) => ({ ...previous, [field]: undefined }));
    setError(null);
  };
  const submit = async (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    if (pending.current || invalidLink || googleBusy) return;
    const next: FieldErrors = {};
    if (signup && !values.name.trim()) next.name = "Enter your name.";
    if (mode !== "reset" && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(values.email.trim())) next.email = "Enter a valid email address.";
    if (mode !== "forgot" && !values.password) next.password = "Enter your password.";
    else if (newPassword && (values.password.length < 12 || values.password.length > 128)) next.password = "Use between 12 and 128 characters.";
    setFields(next); setError(null);
    const first = Object.keys(next)[0];
    if (first) { event.currentTarget.querySelector<HTMLInputElement>(`[name="${first}"]`)?.focus(); return; }
    pending.current = true; setBusy(true);
    try {
      const email = values.email.trim().toLowerCase();
      if (!recovery) {
        const failure = signup ? await signUp(values.name.trim(), email, values.password) : await signIn(email, values.password);
        if (failure) { setError(failure); return; }
        void navigate(destination, { replace: true });
      } else {
        const response = await fetch(`/api/v1/auth/${mode === "forgot" ? "forgot-password" : "reset-password"}`, {
          method: "POST", credentials: "same-origin", signal: AbortSignal.timeout(15_000),
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify(mode === "forgot" ? { email } : { token, password: values.password }),
        });
        if (!response.ok) { setError(await errorMessage(response, "The request could not be completed. Please try again.")); return; }
        if (mode === "forgot") setMessage("If an account exists for that email, you’ll receive a password reset link. Check your inbox and spam folder. The link expires in 30 minutes.");
        else {
          clearAccount();
          void navigate("/sign-in", { replace: true, state: { message: "Your password was updated. Sign in with your new password." } });
        }
      }
    } catch { setError("We couldn’t reach the account service. Check your connection and try again."); }
    finally { pending.current = false; setBusy(false); }
  };
  function field(id: Field, label: string) {
    const password = id === "password";
    const hint = password && newPassword;
    return <div className="auth-field">
      <label htmlFor={`auth-${id}`}>{label}</label>
      <div className={password ? "auth-password" : undefined}>
        <input id={`auth-${id}`} name={id} ref={id === (signup ? "name" : "email") ? emailRef : undefined} required readOnly={busy || googleBusy}
          type={password ? visible ? "text" : "password" : id === "email" ? "email" : "text"}
          autoComplete={password ? newPassword ? "new-password" : "current-password" : id === "email" ? "username" : "name"}
          placeholder={id === "name" ? "Your name" : id === "email" ? "you@studio.com" : newPassword ? "Create a strong password" : "Enter your password"}
          autoCapitalize={id === "name" ? "words" : "none"} spellCheck={false}
          maxLength={id === "name" ? 100 : id === "email" ? 254 : newPassword ? 128 : undefined}
          aria-invalid={Boolean(fields[id])}
          aria-describedby={[fields[id] ? `auth-${id}-error` : "", hint ? "auth-password-hint" : ""].filter(Boolean).join(" ") || undefined}
          value={values[id]} onChange={(event) => update(id, event.target.value)} />
        {password ? <button className="auth-password-toggle" type="button" aria-controls="auth-password" aria-label={visible ? "Hide password" : "Show password"} aria-pressed={visible} onClick={() => setVisible(!visible)}>{visible ? "Hide" : "Show"}</button> : null}
      </div>
      {hint ? <small id="auth-password-hint">Use 12–128 characters.</small> : null}
      {fields[id] ? <p id={`auth-${id}-error`} className="auth-field-error">{fields[id]}</p> : null}
    </div>;
  }
  if (!recovery && loading) return <AuthShell><p role="status">Checking your session…</p></AuthShell>;
  if (!recovery && account) return <Navigate replace to={destination} />;
  const title = { "sign-in": "Welcome back", "sign-up": "Create your account", forgot: "Reset your password", reset: "Choose a new password" }[mode];
  const heading = message ? "Check your email" : title;
  const headingBreak = heading.lastIndexOf(" ");
  const lede = { "sign-in": "Your next great stencil starts here.", "sign-up": "A little less prep. A lot more creating.", forgot: "Enter your account email and we’ll send you a single-use reset link.", reset: "Choose a unique password. Your existing sessions will be signed out." }[mode];
  const action = { "sign-in": "Sign in", "sign-up": "Create account", forgot: "Send reset link", reset: "Update password" }[mode];
  const progress = { "sign-in": "Signing in…", "sign-up": "Creating account…", forgot: "Sending reset link…", reset: "Updating password…" }[mode];
  return <AuthShell>
    {recovery ? <p className="auth-kicker">Account recovery</p> : null}
    <h1>{heading.slice(0, headingBreak)} <em className="studio-heading-accent">{heading.slice(headingBreak + 1)}</em></h1>
    {!message ? <p className="auth-lede">{lede}</p> : null}
    {signup ? <p className="auth-offer"><Icon name="spark" /><span>2 free generations <span aria-hidden="true">·</span> No card required</span></p> : null}
    {!recovery ? <GoogleSignIn signup={signup} destination={destination} disabled={busy} signIn={signInGoogle} onBusyChange={setGoogleBusy} onSuccess={() => { analytics.track(signup ? "account_created" : "signed_in", { auth_method: "google" }); void navigate(destination, { replace: true }); }} /> : null}
    {typeof state?.message === "string" && !error ? <p className="auth-success" role="status">{state.message}</p> : null}
    {!emailOpen ? <button className="auth-email-option" type="button" disabled={googleBusy} aria-expanded="false" aria-controls="auth-email-form" onClick={() => setEmailOpen(true)}><svg className="icon" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" aria-hidden="true"><rect x="3" y="5" width="18" height="14" rx="3" /><path d="m4 7 8 6 8-6" /></svg>Continue with email</button> : null}
    {message ? <p className="auth-success" role="status">{message}</p> : invalidLink ? <div className="auth-error" role="alert">This reset link is missing or invalid. <Link to="/forgot-password">Request a new link</Link>.</div> : emailOpen ?
      <form id="auth-email-form" className="auth-form" noValidate onSubmit={submit} aria-label={title} aria-busy={busy}>
        {signup ? field("name", "Name") : null}
        {mode !== "reset" ? field("email", "Email") : null}
        {mode !== "forgot" ? field("password", mode === "reset" ? "New password" : "Password") : null}
        {mode === "sign-in" ? <Link className="auth-forgot" to="/forgot-password" state={{ email: values.email }}>Forgot your password?</Link> : null}
        <div className="auth-actions">
          {error ? <div className="auth-error" role="alert" tabIndex={-1} ref={alertRef}><strong>{mode === "sign-in" ? "Unable to sign in" : signup ? "Unable to create account" : "Request unsuccessful"}</strong><span>{error}</span>{mode === "reset" ? <Link to="/forgot-password">Request a new reset link</Link> : null}{signup ? <Link to="/forgot-password" state={{ email: values.email }}>Reset your password</Link> : null}</div> : null}
          <button className="auth-submit" disabled={busy || googleBusy} type="submit">{busy ? progress : action}{!busy ? <Icon name="arrow-right" /> : null}</button>
          <span className="auth-sr-only" role="status">{busy ? progress : ""}</span>
        </div>
      </form> : null}
    <p className="auth-switch">{recovery ? <Link to="/sign-in">Back to sign in</Link> : <>{signup ? "Already have an account?" : "New to Stencil Studio?"} <Link to={signup ? "/sign-in" : "/sign-up"} state={{ from: destination, email: values.email }}>{signup ? "Sign in" : "Create an account"}</Link></>}</p>
    {!recovery ? <p className="auth-legal">{signup ? <>By creating an account, you agree to our <Link to="/terms" target="_blank" rel="noopener">Terms of Use<span className="auth-sr-only"> (opens in a new tab)</span></Link> and acknowledge our <Link to="/privacy" target="_blank" rel="noopener">Privacy Policy<span className="auth-sr-only"> (opens in a new tab)</span></Link>.</> : <><Link to="/terms" target="_blank" rel="noopener">Terms of Use<span className="auth-sr-only"> (opens in a new tab)</span></Link><span aria-hidden="true">·</span><Link to="/privacy" target="_blank" rel="noopener">Privacy Policy<span className="auth-sr-only"> (opens in a new tab)</span></Link></>}</p> : null}
  </AuthShell>;
}

export function SignInPage() { return <AuthFormPage key="sign-in" mode="sign-in" />; }
export function SignUpPage() { return <AuthFormPage key="sign-up" mode="sign-up" />; }
export function ForgotPasswordPage() { return <AuthFormPage key="forgot" mode="forgot" />; }
export function ResetPasswordPage() { return <AuthFormPage key="reset" mode="reset" />; }

export function BillingPage() {
  const { search } = useLocation();
  return <Navigate replace to={`/${search}#pricing`} />;
}
